import * as React from "react"

import { AnimatePresence, motion } from "motion/react"
import { Button, ButtonProps } from "./button"
import { HugeiconsIcon, Icons } from "@/components/icons"
import { useBoolean, useCopyToClipboard } from "usehooks-ts"

import type { SVGProps } from "react"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { useIntl } from "react-intl"

function AnimatedCheckIcon({
  size = 16,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          type: "tween",
          duration: 0.25,
          ease: "easeInOut",
        }}
        stroke="currentColor"
        strokeWidth={2}
        d="M21.801 10A10 10 0 1 1 17 3.335"
      />
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          type: "tween",
          duration: 0.25,
          ease: "easeInOut",
          delay: 0.2,
        }}
        stroke="currentColor"
        strokeWidth={2}
        d="m9 11 3 3L22 4"
      />
    </svg>
  )
}

interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "children"> {
  text: string
  successMessage?: string
  errorMessage?: string
  iconClassName?: string
  iconSize?: number
  showText?: boolean
  copyText?: string
}

export function CopyButton({
  text,
  successMessage,
  errorMessage,
  iconClassName,
  iconSize = 16,
  showText = false,
  copyText: copyTextLabel,
  className,
  variant = "ghost",
  size = "icon",
  ...props
}: CopyButtonProps) {
  const [, copyToClipboard] = useCopyToClipboard()
  const {
    value: showCheck,
    setTrue: showCheckTrue,
    setFalse: showCheckFalse,
  } = useBoolean(false)
  const {
    value: isDisabled,
    setTrue: disableTrue,
    setFalse: disableFalse,
  } = useBoolean(false)
  const intl = useIntl()

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (isDisabled) return

    copyToClipboard(text)
      .then(() => {
        if (successMessage) {
          toast.success(successMessage)
        }
        showCheckTrue()
        disableTrue()
        setTimeout(() => {
          showCheckFalse()
          disableFalse()
        }, 2000)
      })
      .catch(() => {
        toast.error(
          errorMessage ||
            intl.formatMessage(
              { id: "common.copyFailed" },
              { defaultValue: "Failed to copy" }
            )
        )
      })
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      disabled={isDisabled}
      className={cn("gap-1.5", className)}
      {...props}
    >
      {showText && (copyTextLabel || intl.formatMessage({ id: "common.copy" }))}
      <span className="relative inline-flex items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          {showCheck ? (
            <motion.span
              key="check"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="inline-flex text-(--success)"
            >
              <AnimatedCheckIcon size={iconSize} className={iconClassName} />
            </motion.span>
          ) : (
            <motion.span
              key="copy"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="inline-flex"
            >
              <HugeiconsIcon
                icon={Icons.Copy01}
                size={iconSize}
                className={iconClassName}
              />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </Button>
  )
}
